import React from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  SimpleGrid,
  Icon,
  Badge,
  Container,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaCheck, FaTimes, FaExternalLinkAlt } from 'react-icons/fa';

const MotionBox = motion(Box);

// Codeverse Mentor Section Component
export const CodeverseMentorSection = ({ data }) => {
  return (
    <Box py={{ base: 16, md: 20 }} bg="white" position="relative" overflow="hidden">
      {/* Background decorations */}
      <Box
        position="absolute"
        top="10%"
        right="5%"
        w="200px"
        h="200px"
        bg="purple.200"
        opacity="0.1"
        borderRadius="full"
        filter="blur(60px)"
      />
      <Box
        position="absolute"
        bottom="10%"
        left="5%"
        w="250px"
        h="250px"
        bg="blue.200"
        opacity="0.1"
        borderRadius="full"
        filter="blur(60px)"
      />

      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Header */}
          <Box textAlign="center" maxW="3xl">
            <Badge
              mb={4}
              px={4}
              py={2}
              bg="purple.500"
              color="white"
              fontSize="sm"
              fontWeight="700"
              borderRadius="full"
              textTransform="uppercase"
            >
              {data.badge}
            </Badge>
            <Heading size="2xl" fontWeight="900" color="brand.800" mb={4}>
              {data.title}
            </Heading>
            <Text fontSize="xl" color="brand.600" lineHeight="1.8">
              {data.subtitle}
            </Text>
          </Box>

          {/* Comparison Table */}
          <Box w="full" bg="brand.50" borderRadius="2xl" p={{ base: 6, md: 8 }} boxShadow="0 4px 20px rgba(0,0,0,0.08)">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0}>
              {/* Header Row */}
              <Box></Box>
              <Box
                bg="purple.500"
                color="white"
                p={4}
                textAlign="center"
                borderTopLeftRadius={{ base: 'xl', md: 'none' }}
                borderTopRightRadius={{ base: 'xl', md: 'none' }}
              >
                <Heading size="md" fontWeight="800">
                  {data.comparisonTable.codeverseColumn}
                </Heading>
              </Box>
              <Box
                bg="brand.700"
                color="white"
                p={4}
                textAlign="center"
              >
                <Heading size="md" fontWeight="800">
                  {data.comparisonTable.otherToolsColumn}
                </Heading>
              </Box>

              {/* Comparison Rows */}
              {data.comparisonTable.rows.map((row, idx) => (
                <React.Fragment key={idx}>
                  <Box
                    p={4}
                    bg={idx % 2 === 0 ? 'white' : 'brand.100'}
                    display="flex"
                    alignItems="center"
                    borderLeft="4px solid"
                    borderColor="purple.500"
                  >
                    <Text fontWeight="600" color="brand.700" fontSize="sm">
                      {row.feature}
                    </Text>
                  </Box>
                  <Box
                    p={4}
                    bg={idx % 2 === 0 ? 'white' : 'brand.100'}
                    textAlign="center"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaCheck} color="green.500" boxSize={6} />
                  </Box>
                  <Box
                    p={4}
                    bg={idx % 2 === 0 ? 'white' : 'brand.100'}
                    textAlign="center"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaTimes} color="red.400" boxSize={6} />
                  </Box>
                </React.Fragment>
              ))}
            </SimpleGrid>
          </Box>

          {/* Key Features Grid */}
          <Box w="full">
            <Heading size="xl" fontWeight="900" color="brand.800" mb={8} textAlign="center">
              {data.featuresTitle}
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {data.features.map((feature, idx) => (
                <MotionBox
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                >
                  <Box
                    p={6}
                    bg="white"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor="purple.200"
                    h="full"
                    _hover={{
                      borderColor: 'purple.500',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(128,90,213,0.15)',
                    }}
                    transition="all 0.3s"
                  >
                    <Icon as={FaLightbulb} boxSize={8} color="purple.500" mb={4} />
                    <Heading size="sm" color="brand.700" mb={3}>
                      {feature.title}
                    </Heading>
                    <Text color="brand.500" fontSize="sm" lineHeight="1.7">
                      {feature.description}
                    </Text>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box>

          {/* Free Access Highlight */}
          <Box
            w="full"
            bg="gradient.purple"
            bgGradient="linear(to-r, purple.600, purple.700)"
            borderRadius="2xl"
            p={8}
            textAlign="center"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              w="300px"
              h="300px"
              bg="whiteAlpha.200"
              borderRadius="full"
              filter="blur(60px)"
            />
            <VStack spacing={4} position="relative" zIndex={1}>
              <Badge
                px={4}
                py={2}
                bg="accent.gold"
                color="brand.800"
                fontSize="md"
                fontWeight="700"
                borderRadius="full"
              >
                {data.freeAccess.badge}
              </Badge>
              <Heading size="xl" color="white" fontWeight="900">
                {data.freeAccess.title}
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.900" maxW="2xl">
                {data.freeAccess.description}
              </Text>
              <Button
                as="a"
                href={data.freeAccess.link}
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                bg="white"
                color="purple.600"
                rightIcon={<Icon as={FaExternalLinkAlt} />}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                }}
                mt={4}
              >
                {data.freeAccess.buttonText}
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default CodeverseMentorSection;
